import React, { useEffect, useRef, useState } from "react";
import HttpManager from "../../../services/HttpManager";

/**
 * Nota de voz como en WhatsApp (Esteban, 2026-10-06: "la nota de voz está bien buggy, se corta o brinca").
 * Antes era un <audio> con la URL temporal de Spaces: vencía a los 15 min y a media nota se cortaba. Ahora se baja
 * el archivo completo cuando la burbuja aparece en pantalla y suena desde la memoria del navegador: adelantar es
 * instantáneo y no se corta. Se baja antes del play porque Safari/Chrome bloquean un play que espera una descarga.
 * Una sola suena a la vez. Safari del iPhone no abre OGG: ahí se pide convertida a M4A.
 */
let sonando: HTMLAudioElement | null = null;
const BARRAS = 36;
// La onda de cada nota (alturas 0–1), calculada una vez por sesión: decodificar es barato pero no gratis.
const ondas = new Map<number, number[]>();
const ondaPlana = Array.from({ length: BARRAS }, () => 0.18);
/** Alturas de la onda: el volumen (RMS) de cada tramo, normalizado al más fuerte. */
const calcularOnda = async (blob: Blob): Promise<number[] | null> => {
	try {
		const AC: any = (window as any).AudioContext || (window as any).webkitAudioContext;
		if (!AC) return null;
		const ctx = new AC();
		const ab = await blob.arrayBuffer();
		// Forma con callbacks: Safari viejo no devuelve promesa en decodeAudioData.
		const buf: AudioBuffer = await new Promise<AudioBuffer>((ok, mal) => ctx.decodeAudioData(ab, ok, mal));
		try { ctx.close(); } catch { /* ya cerrado */ }
		const d = buf.getChannelData(0);
		const paso = Math.max(1, Math.floor(d.length / BARRAS));
		const rms: number[] = [];
		for (let i = 0; i < BARRAS; i++) {
			let suma = 0;
			for (let j = i * paso; j < Math.min(d.length, (i + 1) * paso); j++) suma += d[j] * d[j];
			rms.push(Math.sqrt(suma / paso));
		}
		const max = Math.max(...rms, 0.0001);
		return rms.map((v) => Math.max(0.12, Math.min(1, v / max)));
	} catch { return null; }
};
const VELOCIDADES = [1, 1.5, 2];
const tiempo = (s: number) => { if (!isFinite(s) || s < 0) s = 0; const m = Math.floor(s / 60); return m + ":" + String(Math.floor(s % 60)).padStart(2, "0"); };
const sinOgg = () => { try { return !document.createElement("audio").canPlayType("audio/ogg; codecs=opus"); } catch { return false; } };

const NotaVoz: React.FC<{ campusId: number; prospectId: number; eventId: number; mime?: string; duracion?: number | null }> = ({ campusId, prospectId, eventId, mime, duracion }) => {
	const audio = useRef<HTMLAudioElement | null>(null);
	const caja = useRef<HTMLDivElement | null>(null);
	const pidiendo = useRef<Promise<string | null> | null>(null);
	const [src, setSrc] = useState<string | null>(null);
	const [cargando, setCargando] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [tocando, setTocando] = useState(false);
	const [t, setT] = useState(0);
	const [dur, setDur] = useState<number>(Number(duracion) || 0);
	const [vel, setVel] = useState(1);
	const [onda, setOnda] = useState<number[]>(() => ondas.get(eventId) || ondaPlana);

	// Al quitar la burbuja: se calla y se suelta la memoria. (Solo al desmontar: pausar al cambiar src cortaba el play.)
	const srcRef = useRef<string | null>(null);
	srcRef.current = src;
	useEffect(() => () => {
		const a = audio.current;
		if (a && sonando === a) sonando = null;
		a?.pause();
		if (srcRef.current) URL.revokeObjectURL(srcRef.current);
	}, []);

	const bajar = (): Promise<string | null> => {
		if (src) return Promise.resolve(src);
		if (pidiendo.current) return pidiendo.current;
		setCargando(true); setError(null);
		const ogg = !mime || /ogg|opus/i.test(mime);
		pidiendo.current = HttpManager.getInstance().prospectsService.mediaArchivo(campusId, prospectId, eventId, ogg && sinOgg() ? "m4a" : "")
			.then((r) => {
				const u = URL.createObjectURL(r.data);
				if (audio.current) audio.current.src = u;
				setSrc(u);
				if (!ondas.has(eventId)) calcularOnda(r.data).then((o) => { if (o) { ondas.set(eventId, o); setOnda(o); } });
				return u;
			})
			.catch(() => { setError("No se pudo bajar la nota. Toca para reintentar."); return null; })
			.finally(() => { setCargando(false); pidiendo.current = null; });
		return pidiendo.current;
	};

	// Se baja sola al acercarse a la pantalla (como las fotos), así el play es inmediato.
	useEffect(() => {
		const el = caja.current;
		if (!el || src) return;
		if (typeof IntersectionObserver === "undefined") { bajar(); return; }
		const io = new IntersectionObserver((v) => { if (v.some((x) => x.isIntersecting)) { io.disconnect(); bajar(); } }, { rootMargin: "300px" });
		io.observe(el);
		return () => io.disconnect();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [eventId]);

	const tocar = (a: HTMLAudioElement) => {
		if (sonando && sonando !== a) sonando.pause();
		sonando = a;
		a.playbackRate = vel;
		a.play().catch((e) => {
			// Un play bloqueado por el navegador (no hubo toque directo) no es error: queda lista para tocar otra vez.
			if (e?.name !== "NotAllowedError" && e?.name !== "AbortError") setError("Este navegador no pudo reproducirla.");
		});
	};

	const alternar = async () => {
		const a = audio.current;
		if (!a) return;
		if (!a.paused) { a.pause(); return; }
		if (src) { tocar(a); return; }   // ya bajada: suena dentro del mismo toque
		const u = await bajar();
		if (u && audio.current) tocar(audio.current);
	};

	const mover = async (v: number) => {
		const a = audio.current;
		if (!a) return;
		if (!src) {
			const u = await bajar();
			if (!u) return;
			if (a.readyState < 1) await new Promise((ok) => a.addEventListener("loadedmetadata", ok, { once: true }));
		}
		a.currentTime = v;
		setT(v);
	};

	const cambiarVel = () => {
		const n = VELOCIDADES[(VELOCIDADES.indexOf(vel) + 1) % VELOCIDADES.length];
		setVel(n);
		if (audio.current) audio.current.playbackRate = n;
	};

	const total = dur || 0;
	return (
		<div ref={caja} className={"cv-voz" + (error ? " con-error" : "")} onTouchStart={(e) => e.stopPropagation()}>
			<audio ref={audio} preload="auto"
				onPlay={() => setTocando(true)} onPause={() => setTocando(false)}
				onEnded={() => { setTocando(false); setT(0); if (audio.current) audio.current.currentTime = 0; }}
				onTimeUpdate={(e) => setT(e.currentTarget.currentTime)}
				onLoadedMetadata={(e) => { const d = e.currentTarget.duration; if (isFinite(d) && d > 0) setDur(d); }}
				onDurationChange={(e) => { const d = e.currentTarget.duration; if (isFinite(d) && d > 0) setDur(d); }} />
			<button className="cv-voz-play" onClick={alternar} aria-busy={cargando} aria-label={tocando ? "Pausar" : "Reproducir nota de voz"}>
				{cargando ? <span className="cv-voz-gira" /> : tocando ? (
					<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><rect x="6" y="5" width="4" height="14" rx="1" fill="currentColor" /><rect x="14" y="5" width="4" height="14" rx="1" fill="currentColor" /></svg>
				) : (
					<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M8 5.5v13a1 1 0 0 0 1.5.86l10.2-6.5a1 1 0 0 0 0-1.72L9.5 4.64A1 1 0 0 0 8 5.5z" fill="currentColor" /></svg>
				)}
			</button>
			<div className="cv-voz-cuerpo">
				<div className="cv-voz-onda">
					{onda.map((h, i) => <span key={i} className={total && (i + 0.5) / onda.length <= t / total ? "oida" : ""} style={{ height: Math.round(4 + h * 22) + "px" }} />)}
					<span className="cv-voz-punto" style={{ left: total ? (Math.min(t, total) / total) * 100 + "%" : "0%" }} aria-hidden="true" />
					<input className="cv-voz-barra" type="range" min={0} max={total || 1} step={0.05} value={Math.min(t, total || 1)}
						onChange={(e) => mover(Number(e.target.value))} aria-label="Avanzar en la nota de voz" />
				</div>
				<span className="cv-voz-tiempo">{error ? error : tocando || t > 0 ? tiempo(t) + (total ? " / " + tiempo(total) : "") : total ? tiempo(total) : "Nota de voz"}</span>
			</div>
			<button className="cv-voz-vel" onClick={cambiarVel} aria-label="Velocidad">{vel === 1 ? "1×" : vel + "×"}</button>
		</div>
	);
};

export default NotaVoz;
